import React, { useState, useEffect } from 'react';
import { HeadphonesIcon, CheckCircle, RefreshCw, CheckSquare, Trash2, X } from 'lucide-react';
import api from '../api';

const SYSTEM_BADGES = {
  AP: { label: 'App', cls: 'bg-primary bg-opacity-10 text-primary' },
  CA: { label: 'Campus', cls: 'bg-info bg-opacity-10 text-info' },
};

const SupportDashboard = ({ onClose }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unresolved');
  const [systemFilter, setSystemFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  // Resolve modal state
  const [resolvingTicket, setResolvingTicket] = useState(null);
  const [resolveSubject, setResolveSubject] = useState('');
  const [resolveBody, setResolveBody] = useState('');

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get('support/');
      setTickets(res.data);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const openResolveModal = (ticket) => {
    setResolvingTicket(ticket);
    setResolveSubject(`Consulta de soporte técnico resuelta – Ticket #${ticket.id}`);
    setResolveBody(`Hola ${ticket.nombre || ''},\n\nTu consulta de soporte técnico (Ticket #${ticket.id}) ha sido resuelta.\n\nProblema reportado: ${ticket.error_type_display}\n\nSaludos,\nEquipo DiploPuentes`);
  };

  const handleResolveConfirm = async () => {
    if (!resolvingTicket) return;
    setActionLoading(resolvingTicket.id + 'resolve');
    try {
      await api.post(`support/${resolvingTicket.id}/resolve/`, {
        subject: resolveSubject,
        body: resolveBody,
      });
      setResolvingTicket(null);
      fetchTickets();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al marcar como resuelto.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('¿Eliminar este ticket? Esta acción no se puede deshacer.')) return;
    setActionLoading(ticketId + 'delete');
    try {
      await api.delete(`support/${ticketId}/`);
      fetchTickets();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar el ticket.');
    } finally {
      setActionLoading(null);
    }
  };

  const unresolvedCount = tickets.filter(t => !t.is_resolved).length;

  const filtered = tickets.filter(t => {
    if (filter === 'unresolved' && t.is_resolved) return false;
    if (filter === 'resolved' && !t.is_resolved) return false;
    if (systemFilter !== 'all' && t.system_type !== systemFilter) return false;
    return true;
  });

  return (
    <>
      <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10500 }}>
        <div
          className="modal-content glassmorphism animate-fade-in"
          onClick={e => e.stopPropagation()}
          style={{ maxWidth: '100vw', width: '100%', maxHeight: '100vh', height: '100vh', display: 'flex', flexDirection: 'column', borderRadius: 0 }}
        >
          <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center p-4">
            <h5 className="modal-title section-title fs-5 mb-0 d-flex align-items-center gap-2">
              <HeadphonesIcon size={22} className="text-warning" />
              Reportes de Soporte Técnico
              {unresolvedCount > 0 && (
                <span className="badge bg-danger rounded-pill ms-1">{unresolvedCount} sin resolver</span>
              )}
            </h5>
            <div className="d-flex gap-2 align-items-center">
              <button className="btn btn-sm btn-outline-secondary border-0" onClick={fetchTickets} title="Actualizar">
                <RefreshCw size={14} />
              </button>
              <button className="btn-close" onClick={onClose} />
            </div>
          </div>

          {/* Filters */}
          <div className="px-4 pb-2 d-flex flex-column gap-2">
            <div className="d-flex gap-2 flex-wrap">
              {[
                { key: 'unresolved', label: `Sin resolver (${unresolvedCount})` },
                { key: 'resolved',   label: 'Resueltos' },
                { key: 'all',        label: `Todos (${tickets.length})` },
              ].map(f => (
                <button
                  key={f.key}
                  className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {[
                { key: 'all', label: 'App y Campus' },
                { key: 'AP',  label: 'Solo App' },
                { key: 'CA',  label: 'Solo Campus' },
              ].map(f => (
                <button
                  key={f.key}
                  className={`btn btn-sm ${systemFilter === f.key ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => setSystemFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-auto px-4 pb-4" style={{ flex: 1 }}>
            {loading ? (
              <div className="text-center py-5 text-muted">Cargando...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-5 text-muted small">No hay reportes para mostrar.</div>
            ) : (
              <div className="d-flex flex-column gap-3 mt-2">
                {filtered.map(ticket => (
                  <div
                    key={ticket.id}
                    className={`card border-0 shadow-sm ${ticket.is_resolved ? 'opacity-75' : ''}`}
                  >
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                        <div>
                          {(ticket.nombre || ticket.apellido) && (
                            <div className="fw-semibold small">
                              {ticket.apellido}{ticket.apellido && ticket.nombre ? ', ' : ''}{ticket.nombre}
                              {ticket.dni && <span className="text-muted fw-normal ms-2">DNI {ticket.dni}</span>}
                            </div>
                          )}
                          <span className="fw-bold text-primary small">{ticket.email}</span>
                          <span className="text-muted small ms-2">
                            {new Date(ticket.created_at).toLocaleString('es-AR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {ticket.is_resolved && (
                            <span className="badge text-bg-success d-flex align-items-center gap-1">
                              <CheckCircle size={12} /> Resuelto
                            </span>
                          )}
                          <span className="badge text-bg-secondary">#{ticket.id}</span>
                        </div>
                      </div>

                      {/* Problem */}
                      <div className="mb-2">
                        {ticket.system_type && (
                          <span className={`badge small me-1 ${SYSTEM_BADGES[ticket.system_type]?.cls}`}>
                            {SYSTEM_BADGES[ticket.system_type]?.label ?? ticket.system_type_display}
                          </span>
                        )}
                        <span className="badge bg-primary bg-opacity-10 text-primary small">
                          {ticket.error_type_display}
                        </span>
                        {ticket.diploma_name && (
                          <span className="badge bg-warning bg-opacity-15 text-warning-emphasis small ms-1">
                            {ticket.diploma_name}
                          </span>
                        )}
                        {ticket.other_description && (
                          <p className="small text-muted mt-1 mb-0 ps-1">"{ticket.other_description}"</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="mt-2 d-flex justify-content-end align-items-center gap-2">
                        {ticket.is_resolved && ticket.resolved_by_name && (
                          <span className="text-muted me-auto" style={{ fontSize: 11 }}>
                            Resuelto por {ticket.resolved_by_name}
                            {ticket.resolved_at && ` · ${new Date(ticket.resolved_at).toLocaleString('es-AR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}`}
                          </span>
                        )}
                        {!ticket.is_resolved && (
                          <button
                            className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                            disabled={!!actionLoading}
                            onClick={() => openResolveModal(ticket)}
                          >
                            <CheckSquare size={13} />
                            Marcar como resuelto
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                          disabled={!!actionLoading}
                          onClick={() => handleDelete(ticket.id)}
                        >
                          <Trash2 size={13} />
                          {actionLoading === ticket.id + 'delete' ? '...' : 'Eliminar'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resolve modal */}
      {resolvingTicket && (
        <div className="modal-overlay" style={{ zIndex: 11000 }} onClick={() => setResolvingTicket(null)}>
          <div
            className="modal-content glassmorphism animate-fade-in p-4"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 560, width: '95%' }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                <CheckSquare size={18} className="text-success" />
                Marcar como resuelto — Ticket #{resolvingTicket.id}
              </h6>
              <button className="btn-close" onClick={() => setResolvingTicket(null)} />
            </div>

            <p className="small text-muted mb-3">
              Completá el correo que se enviará al solicitante
              {resolvingTicket.diploma_name ? ` y al coordinador de ${resolvingTicket.diploma_name}` : ''}.
              Si el correo no está registrado en el sistema, no se enviará nada.
            </p>

            <div className="mb-3">
              <label className="form-label fw-semibold small">Asunto</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={resolveSubject}
                onChange={e => setResolveSubject(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold small">Mensaje</label>
              <textarea
                className="form-control form-control-sm"
                rows={6}
                value={resolveBody}
                onChange={e => setResolveBody(e.target.value)}
              />
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setResolvingTicket(null)}>
                Cancelar
              </button>
              <button
                className="btn btn-sm btn-success d-flex align-items-center gap-1"
                disabled={!!actionLoading}
                onClick={handleResolveConfirm}
              >
                <CheckCircle size={14} />
                {actionLoading === resolvingTicket.id + 'resolve' ? 'Guardando...' : 'Confirmar y enviar correo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportDashboard;
